import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Loader2,
  MapPin, Paperclip, Pencil, RefreshCw, Trash2,
  Upload, Zap, Building2, ReceiptText, Gauge, ShieldCheck, History, CalendarX,
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '../../../foundation/altzero_base/base/components/ui/card';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { Input } from '../../../foundation/altzero_base/base/components/ui/input';
import { Label } from '../../../foundation/altzero_base/base/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../foundation/altzero_base/base/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../../foundation/altzero_base/base/components/ui/dialog';
import { cn } from '../../../foundation/altzero_base/base/lib/utils';
import { supabase } from '../../../foundation/altzero_base/base/utils/supabaseClient';
import { siteService, effectiveSiteStatus } from '../services/siteService';
import { salesforceService, type SfUser } from '../services/salesforceService';
import { lossFactorService } from '../../scopingAi/services/lossFactorService';
import { attachmentService } from '../services/attachmentService';
import { billService } from '../services/billService';
import { siteNetworkService, distributorsMatch } from '../services/siteNetworkService';
import { pickMlfForDate, pickDlfForDate, todayIso, type PickedFactor } from '../utils/lossFactorFy';
import { ContractsTabs } from '../components/contracts/ContractsTabs';
import { BillRunDetail } from '../components/bills/BillRunDetail';
import { NemAnalysisStepper } from '../components/NemAnalysisStepper';
import { ManualUsageEntry } from '../components/ManualUsageEntry';
import { SiteInvoicesTab } from '../components/SiteInvoicesTab';
import { DataFreshness } from '../components/DataFreshness';
import { useBackTarget } from '../utils/backTarget';
// What is left is what the CATALOG needs: one entry per code for the network
// card's picker, and the values already in use for the definition dialog's
// suggest inputs. The assignment helpers moved with the card (job 35).
import { uniqueByTariffCode, distinctFieldValues } from '../utils/networkTariffs';
import { AuditHistoryPanel } from '../components/AuditHistoryPanel';
import { SuggestInput } from '../components/common/SuggestInput';
import { ATTACHMENT_CATEGORY_LABELS } from '../types/billValidationTypes';
import type {
  BvSite, BvAttachment, BvAttachmentCategory,
  BvSiteNetwork, BvSiteNetworkFormData, BvSiteDma,
} from '../types/billValidationTypes';

// ─── Network tariff form constants/helpers ─────────────────────────────────────

const EMPTY_NETWORK_FORM: BvSiteNetworkFormData = {
  tariff_code: '', tariff_name: '',
  distributor: '', description: '',
  name: '', channel: '', market_region: '', category: '',
  days_mapping_id: '', times_mapping_id: '', months_mapping_id: '',
  time_type_id: '', treatment_id: '',
  bucket_key: '', bill_category: '', demand_metric: '', interval_min: '',
  event_type: '', treatment_params: '', notes: '',
  open_to_new_applicants: true, price: '', price_type: '',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const ENROLMENT_STYLES: Record<string, string> = {
  Enrolled: 'bg-green-100 text-green-800',
  'Pending Enrolment': 'bg-yellow-100 text-yellow-800',
  'Not Enrolled': 'bg-gray-100 text-gray-600',
  'Pending Cancellation': 'bg-orange-100 text-orange-700',
  Cancelled: 'bg-red-100 text-red-700',
};

function DetailRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex justify-between py-2 border-b border-gray-50 last:border-0">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium text-gray-900 text-right max-w-[60%] break-words">
        {value || <span className="text-gray-300 font-normal">—</span>}
      </dd>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function SiteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Arrived from the Bills list, the dashboard, or an entity's site list? Go back
  // there. Otherwise the sites list (jobs 16/17).
  const back = useBackTarget({ label: 'Back to Sites', to: '/bill-validation/sites' });

  const [site, setSite] = useState<BvSite | null>(null);
  const [attachments, setAttachments] = useState<BvAttachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOverAttach, setDragOverAttach] = useState(false);
  const [uploadCategory, setUploadCategory] = useState<BvAttachmentCategory>('invoice');
  const [activeTab, setActiveTab] = useState<'billruns' | 'invoices' | 'nem' | 'site' | 'history'>('billruns');
  const [nemMode, setNemMode] = useState<'analysis' | 'manual'>('analysis');
  const fileRef = useRef<HTMLInputElement>(null);

  // Salesforce modal state
  const [sfModalOpen, setSfModalOpen] = useState(false);
  const [sfData, setSfData] = useState<Record<string, unknown> | null>(null);
  const [sfOwner, setSfOwner] = useState<SfUser | null>(null);
  const [sfLoading, setSfLoading] = useState(false);
  const [sfError, setSfError] = useState<string | null>(null);

  // Salesforce sync state
  const [sfSyncing, setSfSyncing] = useState(false);
  const [sfSyncMsg, setSfSyncMsg] = useState<string | null>(null);

  // Contracts live inside the "Site Additional Details" tab, so jumping there
  // from a bill run needs both a tab switch and a scroll.
  const contractsRef = useRef<HTMLDivElement>(null);
  const openContracts = () => {
    setActiveTab('site');
    requestAnimationFrame(() => contractsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };

  // Site deletion — needed to clear NMIs added only for testing.
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deps, setDeps] = useState<{ bills: number; contracts: number; contacts: number } | null>(null);

  useEffect(() => {
    if (!confirmDelete || !id) return;
    setDeps(null);
    siteService.countDependents(id).then((r) => { if (r.success) setDeps(r.data ?? null); });
  }, [confirmDelete, id]);

  const handleDelete = async () => {
    if (!id) return;
    setDeleting(true);
    setDeleteError(null);
    const r = await siteService.delete(id);
    setDeleting(false);
    if (!r.success) { setDeleteError(r.error ?? 'Delete failed'); return; }
    navigate('/bill-validation/sites');
  };

  // SF preview/diff modal state
  type SfChange = { field: string; label: string; table: string; current: string | null; proposed: string | null };
  const [sfPreviewOpen, setSfPreviewOpen] = useState(false);
  const [sfPreviewChanges, setSfPreviewChanges] = useState<SfChange[]>([]);
  const [sfApplying, setSfApplying] = useState(false);
  // Job 72: which previewed changes the user has approved, by field name — the
  // same names POST /sync-site expects. Erin: "needs ability to select which
  // lines are approved to come through", because Salesforce holds older
  // free-text names that overwrite the controlled ones.
  const [sfApproved, setSfApproved] = useState<Set<string>>(new Set());

  const toggleSfField = (field: string) => {
    setSfApproved((prev) => {
      const next = new Set(prev);
      if (next.has(field)) next.delete(field); else next.add(field);
      return next;
    });
  };

  const getAuthHeaders = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const API = (import.meta as { env: Record<string, string> }).env.VITE_API_URL ?? 'http://localhost:3001';
    return {
      API,
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': 'scopingai',
        'Authorization': `Bearer ${session?.access_token ?? ''}`,
        'x-user-id': session?.user?.id ?? '',
      },
    };
  };

  const handleOpenSfModal = async () => {
    if (!site?.sf_site_id) return;
    setSfModalOpen(true);
    setSfData(null);
    setSfOwner(null);
    setSfError(null);
    setSfLoading(true);
    const result = await salesforceService.getOpportunityAllFields(site.sf_site_id);
    if (result.success && result.data) {
      setSfData(result.data);
      const ownerId = result.data['OwnerId'] as string | undefined;
      if (ownerId) {
        const ownerResult = await salesforceService.getUserById(ownerId);
        if (ownerResult.success) setSfOwner(ownerResult.data ?? null);
      }
    } else {
      setSfError(result.error ?? 'Failed to load Salesforce data');
    }
    setSfLoading(false);
  };

  const handleSfSync = async () => {
    if (!site?.id || !site.sf_site_id || sfSyncing) return;
    setSfSyncing(true);
    setSfSyncMsg(null);
    try {
      const { API, headers } = await getAuthHeaders();
      const resp = await fetch(`${API}/api/salesforce/preview-site`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ siteId: site.id }),
      });
      const json = await resp.json() as { success: boolean; data?: { changes: SfChange[] }; error?: string };
      if (!json.success) throw new Error(json.error ?? 'Preview failed');
      const changes = json.data?.changes ?? [];
      if (changes.length === 0) {
        setSfSyncMsg('Already up to date — no changes from Salesforce');
        setTimeout(() => setSfSyncMsg(null), 4000);
        return;
      }
      setSfPreviewChanges(changes);
      // Everything starts ticked: the previous behaviour was to apply all of
      // them, so opening the dialog and pressing Apply does exactly what it
      // always did. Declining a line is the new, deliberate act.
      setSfApproved(new Set(changes.map((c) => c.field)));
      setSfPreviewOpen(true);
    } catch (err) {
      setSfSyncMsg(err instanceof Error ? err.message : 'Preview failed');
      setTimeout(() => setSfSyncMsg(null), 4000);
    } finally {
      setSfSyncing(false);
    }
  };

  const handleSfApply = async () => {
    if (!site?.id || sfApplying) return;
    // Nothing ticked means nothing to do. The server treats an empty array as
    // "apply none", but sending it would still stamp sf_synced_at and claim a
    // sync happened, so stop here instead.
    if (sfApproved.size === 0) return;
    setSfApplying(true);
    try {
      const { API, headers } = await getAuthHeaders();
      const resp = await fetch(`${API}/api/salesforce/sync-site`, {
        method: 'POST',
        headers,
        // Only the ticked fields. Omitting `fields` entirely would apply all of
        // them, which is what the bulk refresh still does.
        body: JSON.stringify({ siteId: site.id, fields: [...sfApproved] }),
      });
      const json = await resp.json() as { success: boolean; error?: string };
      if (!json.success) throw new Error(json.error ?? 'Sync failed');
      const appliedCount = sfApproved.size;
      const declined = sfPreviewChanges.length - appliedCount;
      setSfPreviewOpen(false);
      setSfPreviewChanges([]);
      setSfApproved(new Set());
      setSfSyncMsg(
        declined > 0
          ? `Applied ${appliedCount} change${appliedCount === 1 ? '' : 's'} from Salesforce · ${declined} declined`
          : 'Synced from Salesforce',
      );
      await load();
      setTimeout(() => setSfSyncMsg(null), 4000);
    } catch (err) {
      setSfSyncMsg(err instanceof Error ? err.message : 'Sync failed');
      setTimeout(() => setSfSyncMsg(null), 4000);
    } finally {
      setSfApplying(false);
    }
  };

  // Bill-run (inline BillRunDetail) state
  const [selectedBillId, setSelectedBillId] = useState<string>('');

  // Network tariff CATALOG state (bv_site_network).
  //
  // Job 35: the site's own tariff assignment, its windows and its charges used
  // to live here too, in three stores that could disagree. They are network
  // contracts now and NetworkTariffSection owns them — what is left on this page
  // is only the catalog: the distributor's published codes, loaded once and
  // handed to the card for its picker, plus the dialog that defines a new one.
  const [allNetworks, setAllNetworks] = useState<BvSiteNetwork[]>([]);
  const [networksLoading, setNetworksLoading] = useState(false);
  const [showNewNetworkDialog, setShowNewNetworkDialog] = useState(false);
  const [editingNetwork, setEditingNetwork] = useState<BvSiteNetwork | null>(null);
  const [newNetworkForm, setNewNetworkForm] = useState<BvSiteNetworkFormData>(EMPTY_NETWORK_FORM);
  const [newNetworkSaving, setNewNetworkSaving] = useState(false);
  const [newNetworkError, setNewNetworkError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    const [siteRes, attRes] = await Promise.all([
      siteService.getById(id),
      attachmentService.list({ siteId: id }),
    ]);
    if (!siteRes.success) { setError(siteRes.error ?? 'Failed to load'); setLoading(false); return; }
    const loadedSite = siteRes.data ?? null;
    setSite(loadedSite);
    // `network_codes` / `network_tariff_dates` are no longer read here: they are
    // derived columns now, written by networkContractService from the site's
    // network contracts. NetworkTariffSection loads those itself.
    if (attRes.success) setAttachments(attRes.data ?? []);
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Default the inline bill-run view to the site's latest bill run.
  useEffect(() => {
    if (!id || selectedBillId) return;
    let mounted = true;
    billService.listBySite(id).then((res) => {
      if (!mounted) return;
      const latest = res.data?.[0];
      if (latest) setSelectedBillId(latest.id);
    });
    return () => { mounted = false; };
  }, [id, selectedBillId]);

  // Auto-fetch missing DLF/MLF values from loss factor tables when site loads.
  // The site stores the CODE; the value is resolved from the loss factor table
  // wherever it is used, per bill and per financial year (see lossFactorFy and
  // shadowCalculationService). Persisting a number here is what made the
  // factors look hard-coded: it was written once and never revisited, so a new
  // financial year never reached the site.
  //
  // This resolves for DISPLAY only and writes nothing. It shows the year in
  // effect today, which is what a bill dated today would be costed at; an
  // individual bill resolves against its own period.
  const [lf, setLf] = useState<{
    mlf: PickedFactor | null; dlf: PickedFactor | null; loading: boolean;
  }>({ mlf: null, dlf: null, loading: false });

  useEffect(() => {
    const bvx = site?.bv_extn;
    const mlfCode = bvx?.mlf_code || bvx?.tni || '';
    const dlfCode = bvx?.dlf_code || '';
    if (!mlfCode && !dlfCode) { setLf({ mlf: null, dlf: null, loading: false }); return; }

    let active = true;
    setLf((p) => ({ ...p, loading: true }));
    (async () => {
      const [mlfRes, dlfRes] = await Promise.all([
        mlfCode ? lossFactorService.getMlf(mlfCode) : Promise.resolve(null),
        dlfCode ? lossFactorService.getDlf(dlfCode) : Promise.resolve(null),
      ]);
      if (!active) return;
      const today = todayIso();
      setLf({
        mlf: mlfRes?.success ? pickMlfForDate(mlfRes.data?.[0], today) : null,
        dlf: dlfRes?.success ? pickDlfForDate(dlfRes.data?.[0], today) : null,
        loading: false,
      });
    })();
    return () => { active = false; };
  }, [site?.id, site?.bv_extn?.mlf_code, site?.bv_extn?.tni, site?.bv_extn?.dlf_code]);

  // An override is a number a person deliberately applied; it wins over the table.
  const mlfOverride = typeof site?.bv_extn?.mlf_override === 'number' ? site.bv_extn.mlf_override : null;
  const dlfOverride = typeof site?.bv_extn?.dlf_override === 'number' ? site.bv_extn.dlf_override : null;
  const shownMlf = mlfOverride ?? lf.mlf?.value ?? null;
  const shownDlf = dlfOverride ?? lf.dlf?.value ?? null;

  // Load all available tariff codes for this org
  useEffect(() => {
    setNetworksLoading(true);
    siteNetworkService.listAll()
      .then(res => { if (res.success) setAllNetworks(res.data ?? []); })
      .finally(() => setNetworksLoading(false));
  }, []);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !id) return;
    setUploading(true);
    const r = await attachmentService.upload(file, { siteId: id, category: uploadCategory });
    if (r.success && r.data) setAttachments((prev) => [r.data!, ...prev]);
    setUploading(false);
    e.target.value = '';
  };

  const handleAttachDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverAttach(false);
    const file = e.dataTransfer.files?.[0];
    if (!file || !id) return;
    setUploading(true);
    const r = await attachmentService.upload(file, { siteId: id, category: uploadCategory });
    if (r.success && r.data) setAttachments((prev) => [r.data!, ...prev]);
    setUploading(false);
  };

  // ── Network tariff catalog handlers ────────────────────────────────────────
  //
  // Assigning a code to the site, dating it and pricing it are
  // NetworkTariffSection's job now — it writes bv_contract directly, so the
  // page no longer holds a dirty buffer of codes and windows to save.

  const handleNewNetwork = () => {
    setEditingNetwork(null);
    setNewNetworkForm({
      ...EMPTY_NETWORK_FORM,
      // The site's own distributor is the answer nine times in ten, and the
      // dialog refuses to save without one.
      distributor: site?.bv_extn?.network_distributor ?? '',
    });
    setNewNetworkError(null);
    setShowNewNetworkDialog(true);
  };

  // The network card knows a tariff CODE; the dialog edits a catalog ROW. One
  // code has several bucket rows since migration 060, so the first is opened —
  // the fields this dialog edits (name, distributor, price) are the ones the
  // rows of a code share.
  const handleEditCatalogCode = (tariffCode: string) => {
    const match = uniqueByTariffCode(allNetworks).find(
      (n) => n.tariff_code.trim().toLowerCase() === tariffCode.trim().toLowerCase(),
    );
    if (match) handleEditNetwork(match);
  };

  const handleEditNetwork = (n: BvSiteNetwork) => {
    setEditingNetwork(n);
    setNewNetworkForm({
      tariff_code:            n.tariff_code,
      tariff_name:            n.tariff_name ?? '',
      distributor:            n.distributor ?? '',
      description:            n.description ?? '',
      name:                   n.name ?? '',
      channel:                n.channel ?? '',
      market_region:          n.market_region ?? '',
      category:               n.category ?? '',
      days_mapping_id:        n.days_mapping_id ?? '',
      times_mapping_id:       n.times_mapping_id ?? '',
      months_mapping_id:      n.months_mapping_id ?? '',
      time_type_id:           n.time_type_id ?? '',
      treatment_id:           n.treatment_id ?? '',
      bucket_key:             n.bucket_key ?? '',
      bill_category:          n.bill_category ?? '',
      demand_metric:          n.demand_metric ?? '',
      interval_min:           n.interval_min != null ? String(n.interval_min) : '',
      event_type:             n.event_type ?? '',
      treatment_params:       n.treatment_params ? JSON.stringify(n.treatment_params) : '',
      notes:                  n.notes ?? '',
      open_to_new_applicants: n.open_to_new_applicants ?? true,
      price:                  n.price != null ? String(n.price) : '',
      price_type:             n.price_type ?? '',
    });
    setShowNewNetworkDialog(true);
  };

  const handleCreateNetwork = async () => {
    if (!newNetworkForm.tariff_code.trim() || !newNetworkForm.distributor.trim()) return;
    setNewNetworkSaving(true);
    setNewNetworkError(null);
    if (editingNetwork) {
      // Update existing
      const res = await siteNetworkService.update(editingNetwork.id, newNetworkForm);
      if (res.success && res.data) {
        setAllNetworks(prev => prev.map(n => n.id === editingNetwork.id ? res.data! : n));
        setNewNetworkForm(EMPTY_NETWORK_FORM);
        setEditingNetwork(null);
        setShowNewNetworkDialog(false);
      } else {
        setNewNetworkError(res.error ?? 'Failed to update tariff code');
      }
    } else {
      // Create new
      const res = await siteNetworkService.create(newNetworkForm);
      if (res.success && res.data) {
        setAllNetworks(prev => [...prev, res.data!].sort((a, b) => a.tariff_code.localeCompare(b.tariff_code)));
        // Defining a catalog code no longer assigns it to the site: the two are
        // separate acts now, and doing both silently is what left a site holding
        // a code nobody had dated. The new code appears in the card's picker
        // immediately; assigning it there is one deliberate click.
        setNewNetworkForm(EMPTY_NETWORK_FORM);
        setShowNewNetworkDialog(false);
      } else {
        setNewNetworkError(res.error ?? 'Failed to create tariff code');
      }
    }
    setNewNetworkSaving(false);
  };

  const handleDeleteAttachment = async (a: BvAttachment) => {
    if (!confirm(`Delete "${a.file_name}"?`)) return;
    await attachmentService.delete(a.id, a.storage_path);
    setAttachments((prev) => prev.filter((x) => x.id !== a.id));
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="w-full py-6 px-4 sm:px-6 lg:px-8 space-y-4">
        <div className="h-6 w-40 bg-gray-100 animate-pulse rounded" />
        <div className="h-32 bg-gray-100 animate-pulse rounded-xl" />
        <div className="grid grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => <div key={i} className="h-24 bg-gray-100 animate-pulse rounded-xl" />)}
        </div>
        <div className="h-80 bg-gray-100 animate-pulse rounded-xl" />
      </div>
    );
  }

  if (error || !site) {
    return (
      <div className="flex flex-col items-center justify-center py-32 text-muted-foreground">
        <MapPin className="h-14 w-14 mb-4 opacity-20" />
        <p className="text-base font-medium mb-1">{error ?? 'Site not found'}</p>
        <Button variant="link" onClick={() => navigate(back.to)}>{back.label}</Button>
      </div>
    );
  }

  const bv = site.bv_extn;
  // Status is stored as a literal and does not track the enrolment dates, so a
  // site whose unenrolment date has passed used to still read "Enrolled".
  const { status, stored: storedStatus, note: statusNote } = effectiveSiteStatus(bv);
  const address = [site.addr_street, site.addr_city, site.addr_state, site.addr_postcode]
    .filter(Boolean).join(', ');

  return (
    <div className="w-full py-6 px-4 sm:px-6 lg:px-8">
      {/* Breadcrumb — back to wherever the user came from (jobs 16/17) */}
      <button
        onClick={() => navigate(back.to)}
        className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-4 transition-colors"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        {back.label}
      </button>

      <div className="space-y-6">

        {/* ── Header card ─────────────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-start gap-4">
              <div className="flex items-start gap-4 flex-1 min-w-0">
                <div className="bg-primary/10 p-3 rounded-lg flex-shrink-0">
                  <Zap className="h-6 w-6 text-primary" />
                </div>
                <div className="min-w-0">
                  <CardTitle className="text-2xl md:text-3xl font-mono">{site.nmi ?? 'No NMI'}</CardTitle>
                  <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground mt-2">
                    <span
                      className={cn('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium', ENROLMENT_STYLES[status] ?? ENROLMENT_STYLES['Not Enrolled'])}
                      title={statusNote ?? undefined}
                    >
                      {status}
                    </span>
                    {statusNote && (
                      <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
                        Saved as "{storedStatus}" — edit the site to correct it
                      </span>
                    )}
                    {bv?.is_embedded && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                        Embedded Network
                      </span>
                    )}
                    {site.client && (
                      // An anchor rather than a button so the browser's own
                      // affordances work: Cmd/Ctrl-click, middle-click and
                      // right-click → Open in new tab. Moving between a site and
                      // its entity is constant, and a button offers none of them.
                      <Link
                        to={`/bill-validation/organisations/${site.client_id}`}
                        // Job 16: tell the entity page where this came from, so its
                        // breadcrumb offers the way back to this site rather than
                        // dropping the user on the client list.
                        state={{
                          from: {
                            label: site.nmi ? `Back to Site ${site.nmi}` : 'Back to Site',
                            to: `/bill-validation/sites/${site.id}`,
                          },
                        }}
                        className="flex items-center gap-1.5 hover:text-primary transition-colors"
                        title="Open entity — Cmd/Ctrl-click for a new tab"
                      >
                        <Building2 className="h-3.5 w-3.5" />
                        {site.client.name}
                      </Link>
                    )}
                    {address && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{address}</span>}
                    {site.sf_site_id && (
                      <button
                        onClick={handleOpenSfModal}
                        className="font-mono text-xs hover:text-primary hover:underline transition-colors cursor-pointer"
                        title="View Salesforce Opportunity"
                      >
                        Op ID: {site.sf_site_id}
                      </button>
                    )}
                    {site.drive_link && (
                      <a href={site.drive_link} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-blue-600 hover:underline">
                        Drive Folder ↗
                      </a>
                    )}
                  </div>
                  <DataFreshness
                    timestamp={site.updated_at ?? site.created_at}
                    source="Site record"
                    className="mt-2"
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-2 md:flex-nowrap md:flex-shrink-0">
                {site.sf_site_id && (
                  <div className="flex flex-col items-end gap-1">
                    <Button
                      variant="outline"
                      className="flex items-center gap-2"
                      disabled={sfSyncing}
                      onClick={handleSfSync}
                      title="Fetch latest data from Salesforce and update site, DMA, contact"
                    >
                      <RefreshCw className={cn('h-4 w-4', sfSyncing && 'animate-spin')} />
                      {sfSyncing ? 'Syncing...' : 'Refresh from Salesforce'}
                    </Button>
                    <DataFreshness
                      timestamp={site.sf_synced_at}
                      source="Salesforce"
                      emptyLabel="never refreshed"
                    />
                    {sfSyncMsg && (
                      <span className={cn('text-xs', sfSyncMsg.includes('ailed') || sfSyncMsg.includes('error') ? 'text-red-500' : 'text-green-600')}>
                        {sfSyncMsg}
                      </span>
                    )}
                  </div>
                )}
                <Button
                  variant="outline"
                  className="flex items-center gap-2"
                  onClick={() => navigate(`/bill-validation/sites/${site.id}/edit`)}
                >
                  <Pencil className="h-4 w-4" />
                  Edit Site
                </Button>
                <Button
                  variant="outline"
                  className="flex items-center gap-2 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                  onClick={() => { setDeleteError(null); setConfirmDelete(true); }}
                >
                  <Trash2 className="h-4 w-4" />
                  Delete Site
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* ── Metrics row ──────────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { label: 'Enrolment Status', value: status, sub: bv?.enrolment_date ? `Since ${new Date(bv.enrolment_date).toLocaleDateString('en-AU')}` : '' },
            { label: 'Distributor', value: bv?.network_distributor ?? '—', sub: '' },
            { label: 'State', value: bv?.state ?? site.addr_state ?? '—', sub: '' },
            { label: 'Attachments', value: String(attachments.length), sub: 'Uploaded files' },
          ].map((m) => (
            <Card key={m.label}>
              <CardContent className="pt-5">
                <p className="text-sm font-medium text-muted-foreground">{m.label}</p>
                <h3 className="text-2xl font-bold text-gray-900 mt-2">{m.value}</h3>
                {m.sub && <p className="text-xs text-muted-foreground mt-1">{m.sub}</p>}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* ── Details grid ──────────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

          {/* Network details */}
          <Card>
            <CardHeader><CardTitle className="text-base">Network Details</CardTitle></CardHeader>
            <CardContent>
              <dl>
                <DetailRow label="NMI (normalised)" value={bv?.nmi_normalised ?? site.nmi} />
                <DetailRow label="TNI" value={bv?.tni} />
                <DetailRow label="State" value={bv?.state ?? site.addr_state} />
                <DetailRow label="Embedded Network" value={bv?.is_embedded ? 'Yes' : 'No'} />
                {/* Editable on the site form and used by the site_reliability
                    treatment threshold, but never shown back here — so a value
                    could be entered and then never verified. */}
                <DetailRow
                  label="Network Reliability"
                  value={bv?.network_reliability
                    ? `${bv.network_reliability.value} ${bv.network_reliability.uom}`
                    : null}
                />
                <DetailRow
                  label="Network Specified Capacity"
                  value={bv?.specified_capacity
                    ? `${bv.specified_capacity.value} ${bv.specified_capacity.uom}`
                    : null}
                />
                <DetailRow
                  label="Critical Peak Demand (CPD)"
                  value={bv?.cpd_demand ? `${bv.cpd_demand.value} ${bv.cpd_demand.uom}` : null}
                />
              </dl>
            </CardContent>
          </Card>

          {/* Loss Factors */}
          <Card>
            <CardHeader><CardTitle className="text-base">Loss Factors</CardTitle></CardHeader>
            <CardContent>
              <dl>
                <DetailRow label="MLF Code" value={bv?.mlf_code || bv?.tni} />
                <DetailRow
                  label={mlfOverride !== null ? 'MLF Value (override)' : 'MLF Value'}
                  value={lf.loading && shownMlf === null ? '…' : shownMlf != null ? String(shownMlf) : null}
                />
                <DetailRow label="DLF Code" value={bv?.dlf_code} />
                <DetailRow
                  label={dlfOverride !== null ? 'DLF Value (override)' : 'DLF Value'}
                  value={lf.loading && shownDlf === null ? '…' : shownDlf != null ? String(shownDlf) : null}
                />
              </dl>
              {(shownMlf != null && shownDlf != null) && (
                <div className="mt-3 pt-3 border-t border-gray-50">
                  <p className="text-xs text-muted-foreground">Combined Loss Factor</p>
                  <p className="text-sm font-mono font-semibold text-gray-900 mt-0.5">
                    {(shownMlf * shownDlf).toFixed(4)}
                  </p>
                </div>
              )}
              {/* Resolved live from the loss factor table, so it always reflects
                  the year now in effect rather than whenever the site was set up. */}
              {(lf.mlf?.fy || lf.dlf?.fy) && (
                <p className="mt-3 text-xs text-muted-foreground">
                  AEMO published values for{' '}
                  {lf.mlf?.fy && lf.dlf?.fy && lf.mlf.fy !== lf.dlf.fy
                    ? `FY ${lf.mlf.fy} (MLF) / FY ${lf.dlf.fy} (DLF)`
                    : `FY ${lf.mlf?.fy ?? lf.dlf?.fy}`}
                  {' '}· resolved from the code, not stored on the site
                </p>
              )}
              {(bv?.mlf_code || bv?.tni) && !lf.loading && lf.mlf?.value == null && mlfOverride === null && (
                <p className="mt-2 text-xs text-amber-700">
                  MLF code does not resolve in the loss factor table — bills for this site cost at 1.0000
                </p>
              )}
              {bv?.dlf_code && !lf.loading && lf.dlf?.value == null && dlfOverride === null && (
                <p className="mt-2 text-xs text-amber-700">
                  DLF code does not resolve in the loss factor table — bills for this site cost at 1.0000
                </p>
              )}
            </CardContent>
          </Card>

          {/* Market + Enrolment */}
          <Card>
            <CardHeader><CardTitle className="text-base">Market & Enrolment</CardTitle></CardHeader>
            <CardContent>
              <dl>
                <DetailRow label="Market Status" value={bv?.market_status} />
                <DetailRow label="Market Region" value={bv?.market_region} />
                <DetailRow label="Customer Classes" value={(bv?.customer_classes ?? []).join(', ') || null} />
                <DetailRow label="BV Status" value={status} />
                <DetailRow label="Enrolment Date" value={bv?.enrolment_date ? new Date(bv.enrolment_date).toLocaleDateString('en-AU') : null} />
                <DetailRow label="Unenrolment Date" value={bv?.unenrolment_date ? new Date(bv.unenrolment_date).toLocaleDateString('en-AU') : null} />
              </dl>
            </CardContent>
          </Card>

          {/* Address */}
          <Card className="md:col-span-2">
            <CardHeader><CardTitle className="text-base">Address</CardTitle></CardHeader>
            <CardContent>
              <dl>
                <DetailRow label="Street" value={site.addr_street} />
                <DetailRow label="City / Suburb" value={site.addr_city} />
                <DetailRow label="State" value={site.addr_state} />
                <DetailRow label="Postcode" value={site.addr_postcode} />
              </dl>
              {site.client && (
                <div className="mt-4 pt-4 border-t border-gray-50">
                  <p className="text-xs text-muted-foreground mb-1">Entity</p>
                  <button
                    onClick={() => navigate(`/bill-validation/organisations/${site.client_id}`, {
                      // Same as the header link — come back to this site, not the
                      // client list (job 16).
                      state: {
                        from: {
                          label: site.nmi ? `Back to Site ${site.nmi}` : 'Back to Site',
                          to: `/bill-validation/sites/${site.id}`,
                        },
                      },
                    })}
                    className="text-sm font-medium text-primary hover:underline flex items-center gap-1.5"
                  >
                    <Building2 className="h-4 w-4" />
                    {site.client.name}
                  </button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Metering & DMA */}
          {(() => {
            const dma: BvSiteDma | null = Array.isArray(site.bv_dma) ? (site.bv_dma[0] ?? null) : null;
            const contact = site.metering_contact;
            const hasDma = dma?.dma_provider || dma?.start_date || dma?.end_date;
            const hasContact = contact?.first_name || contact?.last_name || contact?.email;
            const hasSfFields = site.loa_expiry_date || site.dma_status || site.meter_install_date || site.shield_platinum != null || site.current_retailer || site.current_contract_expiry || site.retailer_contract_start || site.previous_retailer;
            if (!hasDma && !hasContact && !hasSfFields) return null;
            return (
              <Card className="md:col-span-3">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Gauge className="h-4 w-4 text-primary" />
                    Metering & DMA
                    {site.shield_platinum && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 ml-1">
                        <ShieldCheck className="h-3 w-3" /> Shield Platinum
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-x-8">
                    {/* LOA / DMA status */}
                    <dl>
                      <DetailRow label="LOA Expiry Date" value={site.loa_expiry_date ? new Date(site.loa_expiry_date).toLocaleDateString('en-AU') : null} />
                      <DetailRow label="DMA Status" value={site.dma_status} />
                      <DetailRow label="Meter Install Date" value={site.meter_install_date ? new Date(site.meter_install_date).toLocaleDateString('en-AU') : null} />
                    </dl>
                    {/* DMA contract */}
                    <dl>
                      <DetailRow label="DMA Provider" value={dma?.dma_provider} />
                      <DetailRow label="DMA Start" value={dma?.start_date ? new Date(dma.start_date).toLocaleDateString('en-AU') : null} />
                      <DetailRow label="DMA End" value={dma?.end_date ? new Date(dma.end_date).toLocaleDateString('en-AU') : null} />
                    </dl>
                    {/* Metering contact */}
                    <dl>
                      <DetailRow label="Contact Name" value={[contact?.first_name, contact?.last_name].filter(Boolean).join(' ') || null} />
                      <DetailRow label="Contact Email" value={contact?.email} />
                    </dl>
                    {/* Retailer contract */}
                    <dl>
                      <DetailRow label="Current Retailer" value={site.current_retailer} />
                      <DetailRow label="Contract Start" value={site.retailer_contract_start ? new Date(site.retailer_contract_start).toLocaleDateString('en-AU') : null} />
                      <DetailRow label="Contract End" value={site.current_contract_expiry ? new Date(site.current_contract_expiry).toLocaleDateString('en-AU') : null} />
                      <DetailRow label="Previous Retailer" value={site.previous_retailer} />
                    </dl>
                  </div>
                </CardContent>
              </Card>
            );
          })()}

        </div>

        {/* ── Tabbed section: Site Details / Bill Runs ──────────────────────── */}
        <Card>
          <CardHeader className="pb-0">
            <div className="flex items-center justify-between">
              <div className="flex border-b border-gray-200 gap-1 w-full">
                {([
                  { id: 'site' as const, label: 'Site Additional Details', icon: Building2 },
                  { id: 'invoices' as const, label: 'Invoices', icon: Paperclip },
                  { id: 'nem' as const, label: 'Usage (NEM)', icon: Gauge },
                  { id: 'billruns' as const, label: 'Bill Runs', icon: ReceiptText },
                  { id: 'history' as const, label: 'History', icon: History },
                ] as const).map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    onClick={() => setActiveTab(id)}
                    className={cn(
                      'inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors border-b-2 -mb-px',
                      activeTab === id
                        ? 'border-primary text-primary bg-primary/5'
                        : 'border-transparent text-muted-foreground hover:text-gray-700 hover:bg-gray-50',
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </button>
                ))}

                <input ref={fileRef} type="file" className="hidden" onChange={handleFileUpload} />
              </div>
            </div>
          </CardHeader>

          <CardContent className={cn(activeTab === 'site' ? 'p-5' : 'pt-5')}>
            {activeTab === 'billruns' && (
              <BillRunDetail
                billId={selectedBillId}
                onBillIdChange={setSelectedBillId}
                siteId={site.id}
                nmi={site.nmi}
                onOpenContracts={openContracts}
                onOpenInvoices={() => setActiveTab('invoices')}
              />
            )}

            {activeTab === 'invoices' && (
              <SiteInvoicesTab
                siteId={site.id}
                orgId={site.org_id}
                onOpenBillRun={(billId) => { setSelectedBillId(billId); setActiveTab('billruns'); }}
              />
            )}

            {activeTab === 'nem' && (
              <div className="space-y-4">
                {/* Two ways usage arrives: derived from interval data, or typed in.
                    Both belong under Usage (NEM) — neither belongs on a bill run. */}
                <div className="flex flex-wrap items-center gap-3">
                  <div className="inline-flex items-center gap-1 rounded-lg bg-gray-100 p-0.5">
                    {([
                      ['analysis', 'NEM Analysis'],
                      ['manual', 'Enter usage manually'],
                    ] as const).map(([mode, label]) => (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => setNemMode(mode)}
                        className={cn(
                          'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                          nemMode === mode
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-500 hover:text-gray-700',
                        )}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  {/* The Tariff Usage Report by NMI, opened on this site. */}
                  <Link
                    data-testid="site-usage-report-link"
                    to={`/bill-validation/report-hub/tariff-usage?site=${site.id}&nmi=${encodeURIComponent(site.nmi ?? '')}`}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-700 hover:underline"
                  >
                    <CalendarX className="h-3.5 w-3.5" />
                    Usage report
                  </Link>
                </div>

                {nemMode === 'analysis'
                  ? <NemAnalysisStepper siteId={site.id} orgId={site.org_id} nmi={site.nmi} />
                  : <ManualUsageEntry siteId={site.id} />}
              </div>
            )}

            {activeTab === 'history' && (
              // Rolled up: users asking "what happened to this site" don't care
              // whether the change landed on the site, one of its bills, or an
              // invoice assignment.
              <AuditHistoryPanel
                entityType="site"
                entityId={site.id}
                siteId={site.id}
                rollUpSite
                title="Site & bill history"
              />
            )}

            {activeTab === 'site' && (
              <div className="space-y-6">

                {/* ── Contracts ─────────────────────────────────────────────────────
                    Network Tariffs is passed in as a node and rendered inside
                    Distribution Config, which is where the distributor and its
                    rates belong together. The card's assignment/usage state stays
                    here because this component owns the site row it writes to. */}
                <div ref={contractsRef} className="scroll-mt-4">
                  <div className="flex items-center justify-end mb-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1.5 h-7 text-xs"
                      onClick={() => setActiveTab('billruns')}
                    >
                      <ReceiptText className="h-3.5 w-3.5" /> Back to bill runs
                    </Button>
                  </div>
                  <ContractsTabs
                    siteId={site.id}
                    siteState={bv?.state ?? site.addr_state}
                    siteDistributor={bv?.network_distributor}
                    // The dialog that defines a catalog code is still on this
                    // page — its trigger used to live in the network card that
                    // job 35 replaced, so the card gets the callback rather than
                    // the capability being lost to Admin Portal only.
                    onDefineCatalogCode={handleNewNetwork}
                    onEditCatalogCode={handleEditCatalogCode}
                    catalogLoading={networksLoading}
                    // EVERY bucket row, deliberately not deduplicated and not
                    // narrowed by distributor: these are the distributor's
                    // published prices the card shows read-only, and a tariff's
                    // prices ARE its buckets — one row per code would show one
                    // of four. The card matches them to a code itself.
                    catalogRows={allNetworks}
                    // One entry per code, not per bucket — a network contract is
                    // assigned against a CODE. Reuses the catalog already paged in
                    // rather than querying it twice.
                    //
                    // Narrowed to the site's distributor: unfiltered this listed every
                    // code every distributor publishes, thousands of rows, and picking
                    // the site's own tariff out of that is worse than typing it. Codes
                    // already assigned are deliberately NOT dropped — those are exactly
                    // the ones an existing contract is written against.
                    tariffOptions={uniqueByTariffCode(allNetworks)
                      .filter(n => !bv?.network_distributor || !n.distributor
                        || distributorsMatch(n.distributor, bv.network_distributor))
                      .map(n => ({
                        tariff_code: n.tariff_code,
                        tariff_name: n.tariff_name,
                        distributor: n.distributor,
                      }))}
                  />
                </div>

                {/* ── Attachments ───────────────────────────────────────────────── */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Paperclip className="h-4 w-4 text-primary" />
                        Attachments ({attachments.length})
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <Select value={uploadCategory} onValueChange={(v) => setUploadCategory(v as BvAttachmentCategory)}>
                          <SelectTrigger className="w-40 h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {Object.entries(ATTACHMENT_CATEGORY_LABELS).map(([k, v]) => (
                              <SelectItem key={k} value={k}>{v}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button size="sm" className="flex items-center gap-1.5 h-8" onClick={() => fileRef.current?.click()} disabled={uploading}>
                          <Upload className="h-3.5 w-3.5" />
                          {uploading ? 'Uploading…' : 'Upload'}
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div
                      className={cn('relative transition-all', dragOverAttach && 'ring-2 ring-blue-400 ring-inset rounded-b-xl bg-blue-50/30')}
                      onDragOver={(e) => { e.preventDefault(); setDragOverAttach(true); }}
                      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOverAttach(false); }}
                      onDrop={handleAttachDrop}
                    >
                      {dragOverAttach && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none rounded-b-xl">
                          <div className="bg-blue-600 text-white text-sm font-medium px-4 py-2 rounded-lg shadow">
                            Drop to upload as {ATTACHMENT_CATEGORY_LABELS[uploadCategory]}
                          </div>
                        </div>
                      )}
                      {attachments.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                          <Paperclip className="h-10 w-10 mb-3 opacity-20" />
                          <p className="text-sm">No attachments — drag a file here or use Upload</p>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-left text-xs text-muted-foreground font-medium uppercase tracking-wider border-b border-gray-100">
                                <th className="px-6 py-3">File Name</th>
                                <th className="px-6 py-3">Category</th>
                                <th className="px-6 py-3">Size</th>
                                <th className="px-6 py-3">LOA Expiry</th>
                                <th className="px-6 py-3">Uploaded</th>
                                <th className="px-6 py-3 w-28" />
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                              {attachments.map((a) => (
                                <tr key={a.id} className="hover:bg-gray-50/50">
                                  <td className="px-6 py-4 font-medium text-gray-900">{a.file_name}</td>
                                  <td className="px-6 py-4">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                      {ATTACHMENT_CATEGORY_LABELS[a.category]}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 text-muted-foreground text-xs">
                                    {a.file_size_bytes ? `${(a.file_size_bytes / 1024).toFixed(0)} KB` : '—'}
                                  </td>
                                  <td className="px-6 py-4 text-muted-foreground text-xs">
                                    {a.loa_expiry_date ? new Date(a.loa_expiry_date).toLocaleDateString('en-AU') : '—'}
                                  </td>
                                  <td className="px-6 py-4 text-muted-foreground text-xs">
                                    {new Date(a.created_at).toLocaleDateString('en-AU')}
                                  </td>
                                  <td className="px-6 py-4">
                                    <div className="flex items-center gap-3">
                                      <button
                                        onClick={async () => { const url = await attachmentService.getSignedUrl(a.storage_path); if (url) window.open(url, '_blank'); }}
                                        className="text-sm text-primary hover:underline"
                                      >Open</button>
                                      <button onClick={() => handleDeleteAttachment(a)} className="p-1 hover:text-red-600 text-muted-foreground">
                                        <Trash2 className="h-4 w-4" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

              </div>
            )}

          </CardContent>
        </Card>

        {/* ── New Tariff Code dialog ─────────────────────────────────────────── */}
        <Dialog open={showNewNetworkDialog} onOpenChange={open => { if (!open) { setNewNetworkError(null); setEditingNetwork(null); setNewNetworkForm(EMPTY_NETWORK_FORM); } setShowNewNetworkDialog(open); }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingNetwork ? `Edit ${editingNetwork.tariff_code}` : 'New Tariff Code'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="nn-code">Tariff Code <span className="text-red-500">*</span></Label>
                  <Input
                    id="nn-code"
                    className="mt-1 font-mono uppercase"
                    placeholder="e.g. EA010"
                    value={newNetworkForm.tariff_code}
                    onChange={e => setNewNetworkForm(f => ({ ...f, tariff_code: e.target.value.toUpperCase() }))}
                  />
                </div>
                <div>
                  <Label htmlFor="nn-name">Tariff Name</Label>
                  <Input
                    id="nn-name"
                    className="mt-1"
                    placeholder="e.g. Residential flat"
                    value={newNetworkForm.tariff_name}
                    onChange={e => setNewNetworkForm(f => ({ ...f, tariff_name: e.target.value }))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label htmlFor="nn-dist">Distributor</Label>
                  {/* Suggests the distributors the org already has rather than
                      inviting a fourth spelling of Ausgrid. Still free text — the
                      first site on a new network has to be typeable. */}
                  <SuggestInput
                    id="nn-dist"
                    className="mt-1"
                    placeholder="e.g. Ausgrid"
                    options={distinctFieldValues(allNetworks, 'distributor')}
                    value={newNetworkForm.distributor}
                    onChange={v => setNewNetworkForm(f => ({ ...f, distributor: v }))}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="nn-desc">Description</Label>
                <Input
                  id="nn-desc"
                  className="mt-1"
                  placeholder="Optional notes"
                  value={newNetworkForm.description}
                  onChange={e => setNewNetworkForm(f => ({ ...f, description: e.target.value }))}
                />
              </div>

              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider pt-1 border-t">Bucket</p>
              <div className="grid grid-cols-2 gap-3">
                {/* `suggest` names the catalog column to offer values from. Price
                    is a number and bucket Name is per-row prose, so neither has a
                    vocabulary to suggest; the rest do. */}
                {([
                  { id: 'nn-bname', label: 'Name (bucket label)', field: 'name' },
                  { id: 'nn-channel', label: 'Channel (E/B/K/Q/days)', field: 'channel', suggest: 'channel' },
                  { id: 'nn-mregion', label: 'Market Region', field: 'market_region', suggest: 'market_region' },
                  { id: 'nn-category', label: 'Category', field: 'category', suggest: 'category' },
                  { id: 'nn-price', label: 'Price', field: 'price', type: 'number' },
                  { id: 'nn-ptype', label: 'Price Type (unit)', field: 'price_type', suggest: 'price_type' },
                ] as { id: string; label: string; field: keyof BvSiteNetworkFormData; type?: string; suggest?: keyof BvSiteNetwork }[]).map(({ id, label, field, type, suggest }) => (
                  <div key={id}>
                    <Label htmlFor={id}>{label}</Label>
                    {suggest ? (
                      <SuggestInput
                        id={id}
                        className="mt-1"
                        placeholder="—"
                        options={distinctFieldValues(allNetworks, suggest)}
                        value={String(newNetworkForm[field] ?? '')}
                        onChange={v => setNewNetworkForm(f => ({ ...f, [field]: v }))}
                      />
                    ) : (
                      <Input
                        id={id}
                        type={type ?? 'text'}
                        step={type === 'number' ? 'any' : undefined}
                        className="mt-1"
                        placeholder="—"
                        value={String(newNetworkForm[field] ?? '')}
                        onChange={e => setNewNetworkForm(f => ({ ...f, [field]: e.target.value }))}
                      />
                    )}
                  </div>
                ))}

                <div className="col-span-2 space-y-1.5">
                  <p className="text-xs text-muted-foreground">
                    Days, times, months and holidays are now managed in{' '}
                    <button
                      type="button"
                      onClick={() => navigate('/admin')}
                      className="text-primary underline-offset-2 hover:underline"
                    >
                      Admin Portal → Network Tariffs
                    </button>
                    .
                  </p>
                </div>

                <div className="col-span-2 flex items-center gap-3">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={newNetworkForm.open_to_new_applicants}
                    onClick={() => setNewNetworkForm((f) => ({ ...f, open_to_new_applicants: !f.open_to_new_applicants }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${newNetworkForm.open_to_new_applicants ? 'bg-primary' : 'bg-muted'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${newNetworkForm.open_to_new_applicants ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                  <span className="text-sm text-muted-foreground">
                    {newNetworkForm.open_to_new_applicants ? 'Open to new applicants' : 'Closed to new applicants'}
                  </span>
                </div>
              </div>

              {newNetworkError && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{newNetworkError}</p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewNetworkDialog(false)} disabled={newNetworkSaving}>
                Cancel
              </Button>
              <Button
                onClick={() => void handleCreateNetwork()}
                disabled={newNetworkSaving || !newNetworkForm.tariff_code.trim() || !newNetworkForm.distributor.trim()}
              >
                {newNetworkSaving && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
                {newNetworkSaving ? (editingNetwork ? 'Saving…' : 'Creating…') : (editingNetwork ? 'Save Changes' : 'Create & Assign')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </div>

      {/* Salesforce Opportunity modal */}
      <Dialog open={sfModalOpen} onOpenChange={setSfModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              Salesforce Opportunity
              <span className="font-mono text-sm text-muted-foreground font-normal">{site?.sf_site_id}</span>
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto min-h-0 space-y-4">
            {sfLoading && (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Loading from Salesforce…
              </div>
            )}
            {sfError && !sfLoading && (
              <div className="p-4 rounded-md bg-red-50 text-red-700 text-sm">{sfError}</div>
            )}
            {sfData && !sfLoading && (
              <>
                {sfOwner && (
                  <div className="rounded-lg border border-blue-100 bg-blue-50 p-4">
                    <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-2">Opportunity Owner</p>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                      <div>
                        <span className="text-muted-foreground">Name</span>
                        <p className="font-medium text-gray-900">{sfOwner.Name}</p>
                      </div>
                      {sfOwner.Title && (
                        <div>
                          <span className="text-muted-foreground">Title</span>
                          <p className="font-medium text-gray-900">{sfOwner.Title}</p>
                        </div>
                      )}
                      {sfOwner.Email && (
                        <div>
                          <span className="text-muted-foreground">Email</span>
                          <p className="font-medium text-gray-900">{sfOwner.Email}</p>
                        </div>
                      )}
                      {(sfOwner.Phone || sfOwner.MobilePhone) && (
                        <div>
                          <span className="text-muted-foreground">Phone</span>
                          <p className="font-medium text-gray-900">{sfOwner.Phone || sfOwner.MobilePhone}</p>
                        </div>
                      )}
                      {sfOwner.Department && (
                        <div>
                          <span className="text-muted-foreground">Department</span>
                          <p className="font-medium text-gray-900">{sfOwner.Department}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <table className="w-full text-sm">
                  <tbody className="divide-y divide-gray-100">
                    {Object.entries(sfData)
                      .filter(([, v]) => v !== null && v !== '' && typeof v !== 'object')
                      .map(([key, value]) => (
                        <tr key={key} className="hover:bg-gray-50">
                          <td className="py-2 px-3 text-muted-foreground font-medium w-[45%] align-top">{key}</td>
                          <td className="py-2 px-3 text-gray-800 break-words">{String(value)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSfModalOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SF Preview / Diff Modal */}
      <Dialog open={sfPreviewOpen} onOpenChange={(open) => { if (!sfApplying) setSfPreviewOpen(open); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4 text-blue-500" />
              Salesforce Changes Preview
            </DialogTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Review the changes from Salesforce before applying. Only changed fields are shown —
              untick any line you do not want written.
            </p>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto -mx-1 px-1">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="py-2 pr-2 w-8">
                    {/* Select-all. Indeterminate is deliberately not used: a
                        tri-state header checkbox reads as a third option here. */}
                    <input
                      type="checkbox"
                      aria-label="Approve every change"
                      className="h-3.5 w-3.5 accent-blue-600 align-middle cursor-pointer"
                      checked={sfApproved.size === sfPreviewChanges.length && sfPreviewChanges.length > 0}
                      disabled={sfApplying}
                      onChange={(e) => setSfApproved(
                        e.target.checked ? new Set(sfPreviewChanges.map((c) => c.field)) : new Set(),
                      )}
                    />
                  </th>
                  <th className="text-left py-2 pr-3 text-muted-foreground font-medium w-[28%]">Field</th>
                  <th className="text-left py-2 pr-3 text-muted-foreground font-medium w-[33%]">Current</th>
                  <th className="text-left py-2 text-muted-foreground font-medium w-[33%]">Salesforce (New)</th>
                </tr>
              </thead>
              <tbody>
                {sfPreviewChanges.map((c) => {
                  const approved = sfApproved.has(c.field);
                  return (
                    <tr
                      key={`${c.table}.${c.field}`}
                      className={`border-b border-gray-50 hover:bg-gray-50/50 ${approved ? '' : 'opacity-45'}`}
                    >
                      <td className="py-2.5 pr-2 align-top">
                        <input
                          type="checkbox"
                          aria-label={`Approve ${c.label}`}
                          className="h-3.5 w-3.5 accent-blue-600 mt-0.5 cursor-pointer"
                          checked={approved}
                          disabled={sfApplying}
                          onChange={() => toggleSfField(c.field)}
                        />
                      </td>
                      <td className="py-2.5 pr-3">
                        <span className="font-medium text-gray-800">{c.label}</span>
                        <span className="block text-[10px] text-muted-foreground capitalize">{c.table}</span>
                      </td>
                      <td className="py-2.5 pr-3 text-gray-400">
                        {c.current ?? <span className="italic text-gray-300">—</span>}
                      </td>
                      <td className="py-2.5">
                        {/* A declined line keeps its current value, so the green
                            "new" styling would be a lie once unticked. */}
                        <span
                          className={approved
                            ? 'text-green-700 font-medium bg-green-50 px-1.5 py-0.5 rounded'
                            : 'text-gray-400 line-through px-1.5 py-0.5'}
                        >
                          {c.proposed ?? '—'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <DialogFooter className="mt-4 flex gap-2">
            <Button
              variant="outline"
              onClick={() => { setSfPreviewOpen(false); setSfPreviewChanges([]); setSfApproved(new Set()); }}
              disabled={sfApplying}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSfApply}
              // Nothing ticked is not a valid apply — the button says so rather
              // than running a sync that writes nothing.
              disabled={sfApplying || sfApproved.size === 0}
              className="flex items-center gap-2"
            >
              {sfApplying ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              {sfApplying
                ? 'Applying...'
                : sfApproved.size === 0
                  ? 'Nothing selected'
                  : `Apply ${sfApproved.size} of ${sfPreviewChanges.length} Change${sfPreviewChanges.length !== 1 ? 's' : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete site confirmation ─────────────────────────────────────────── */}
      <Dialog open={confirmDelete} onOpenChange={(open) => { if (!open) { setConfirmDelete(false); setDeleteError(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-red-700">Delete this site?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1 text-sm">
            <p className="text-gray-700">
              NMI <span className="font-mono font-semibold">{site.nmi ?? '—'}</span>
              {site.client?.name ? <> at <span className="font-medium">{site.client.name}</span></> : null} will be
              permanently removed. This cannot be undone.
            </p>

            {deps == null ? (
              <p className="text-xs text-gray-400 flex items-center gap-1.5">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking what references this site…
              </p>
            ) : (
              <ul className="text-xs text-gray-600 space-y-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                <li>Bills: <span className="font-semibold">{deps.bills}</span>{deps.bills > 0 && ' — deletion is blocked while bills exist'}</li>
                <li>Contracts: <span className="font-semibold">{deps.contracts}</span>{deps.contracts > 0 && ' — will be deleted with the site'}</li>
                <li>Contacts: <span className="font-semibold">{deps.contacts}</span>{deps.contacts > 0 && ' — will be deleted with the site'}</li>
              </ul>
            )}

            {deleteError && (
              <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{deleteError}</p>
            )}
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setConfirmDelete(false)} disabled={deleting}>Cancel</Button>
            <Button
              onClick={() => void handleDelete()}
              disabled={deleting || deps == null || deps.bills > 0}
              className="bg-red-600 hover:bg-red-700 text-white flex items-center gap-2"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              {deleting ? 'Deleting…' : 'Delete Site'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
